import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { Users, Circle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const OnlineFollowing = ({ compact = false }) => {
  const { token } = useAuth();
  const { onlineUsers, isConnected } = useWebSocket();
  const navigate = useNavigate();
  const [onlineFollowing, setOnlineFollowing] = useState([]);
  const [totalFollowing, setTotalFollowing] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    // Run once on mount even if the WebSocket hasn't connected yet — otherwise
    // the widget stays in `loading=true` forever and never renders any DOM.
    fetchOnlineFollowing();
    // iter_474 — Pause this 30s presence poll while the tab is hidden.
    // WebSocket presence updates already drive real-time changes; the
    // poll is a fallback safety net we don't need to run in background.
    if (isConnected) {
      const interval = setInterval(() => {
        if (document.hidden) return;
        fetchOnlineFollowing();
      }, 30000);
      const onVis = () => { if (document.visibilityState === 'visible') fetchOnlineFollowing(); };
      document.addEventListener('visibilitychange', onVis);
      return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', onVis);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: mount-only fetch / stable closure
  }, [token, isConnected, onlineUsers]);

  const fetchOnlineFollowing = async () => {
    try {
      const response = await axios.get(`${API}/ws/following-online`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnlineFollowing(response.data.online_following || []);
      setTotalFollowing(response.data.total_following || 0);
    } catch (error) {
      console.error('Error fetching online following:', error);
    } finally {
      setLoading(false);
    }
  };

  // No auth → stay invisible (logged-out users).
  if (!token) return null;

  // Loading skeleton — render the shell so QA + slow networks see SOMETHING
  // rather than a blank space. Uses the same testid so callers can reliably
  // assert "the widget mounted".
  if (loading) {
    return compact ? null : (
      <div className="px-4 py-2 flex items-center gap-2" data-testid="online-following-empty">
        <Circle size={6} className="text-[#444] fill-[#444] animate-pulse" />
        <p className="text-[#666] text-xs font-mono">Checking online status…</p>
      </div>
    );
  }

  if (onlineFollowing.length === 0) {
    return compact ? null : (
      <div className="px-4 py-2 flex items-center gap-2" data-testid="online-following-empty">
        <Circle size={6} className="text-[#444] fill-[#444] animate-pulse" />
        <p className="text-[#666] text-xs font-mono">
          {totalFollowing > 0
            ? `0 of ${totalFollowing} followed online`
            : 'Follow creators to see who\'s online'}
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-2 mb-3">
        <div className="relative">
          <Users size={14} className="text-[#888]" />
          <Circle size={6} className="absolute -top-0.5 -right-0.5 text-green-500 fill-green-500 animate-pulse" />
        </div>
        <span className="text-xs font-mono text-[#888] uppercase tracking-wider">
          Following Online ({onlineFollowing.length}{totalFollowing ? ` / ${totalFollowing}` : ''})
        </span>
      </div>
      
      <div className="space-y-2">
        {onlineFollowing.slice(0, compact ? 3 : 10).map(user => (
          <div
            key={user.id}
            onClick={() => navigate(`/profile/${user.id}`)}
            className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#1a1a1a] cursor-pointer transition-colors group"
            data-testid={`online-user-${user.id}`}
          >
            <div className="relative">
              <div className={`w-8 h-8 rounded-full overflow-hidden ${user.is_vip ? 'ring-2 ring-[#00CCFF]' : ''}`}>
                {user.profile_picture ? (
                  <img
                    src={`${BACKEND_URL}/api/profile/picture/${user.profile_picture}`}
                    alt={user.username}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%231a1a1a"/><text x="50" y="55" text-anchor="middle" fill="%23888" font-size="40">♪</text></svg>';
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-[#0a0a0a] flex items-center justify-center">
                    <Users size={14} className="text-[#444]" />
                  </div>
                )}
              </div>
              {/* Online indicator */}
              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-black" />
            </div>
            
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm truncate group-hover:text-[#00CCFF] transition-colors">
                {user.username}
              </p>
              <p className="text-[#666] text-xs truncate">
                {user.is_artist && user.is_producer ? 'Artist & Producer' : user.is_artist ? 'Artist' : user.is_producer ? 'Producer' : 'Fan'}
              </p>
            </div>
          </div>
        ))}
        
        {onlineFollowing.length > (compact ? 3 : 10) && (
          <button
            onClick={() => navigate('/following')}
            className="w-full text-center text-xs text-[#00CCFF] hover:underline py-1"
          >
            +{onlineFollowing.length - (compact ? 3 : 10)} more online
          </button>
        )}
      </div>
    </div>
  );
};

export default OnlineFollowing;
